import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MOIS = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
const VERT = '#10B981';

const OpsGraphInstallations = () => {
  const [data, setData] = useState(null);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const zone = useRef(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/chantiers/dashboard/installations`, { params: { annee } })
      .then((r) => setData(r.data.data))
      .catch(() => setData(null));
  }, [annee]);

  const carte = { background: '#fff', borderRadius: '12px', padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
  if (!data) return <div style={{ ...carte, textAlign: 'center', color: '#9CA3AF', padding: '32px' }}>Chargement…</div>;

  const valeurs = data.par_mois.slice(0, data.mois_actuel);
  const max = Math.max(...data.par_mois, 1);
  const L = 1200, H = 400, mg = { h: 34, b: 26, d: 12, g: 34 };
  const largeur = L - mg.g - mg.d;
  const hauteur = H - mg.h - mg.b;

  const x = (i) => mg.g + (valeurs.length === 1 ? largeur / 2 : (i / (valeurs.length - 1)) * largeur);
  const y = (v) => mg.h + hauteur - (v / max) * hauteur;

  const pts = valeurs.map((v, i) => [x(i), y(v)]);
  let d = pts.length ? `M ${pts[0][0]} ${pts[0][1]}` : '';
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i], [x2, y2] = pts[i + 1];
    const cx = (x1 + x2) / 2;
    d += ` C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
  }
  const aire = pts.length > 1 ? `${d} L ${pts[pts.length - 1][0]} ${mg.h + hauteur} L ${pts[0][0]} ${mg.h + hauteur} Z` : '';
  const graduations = [...new Set([0, Math.ceil(max / 2), max])];

  const bouger = (e) => {
    if (!zone.current || valeurs.length < 2) return;
    const r = zone.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * L;
    const i = Math.round(((px - mg.g) / largeur) * (valeurs.length - 1));
    if (i >= 0 && i < valeurs.length) setInfo({ i, v: valeurs[i] });
  };

  return (
    <div style={carte}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '6px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: 600 }}>
          <TrendingUp size={17} color={VERT} /> Installations réalisées
        </span>
        <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}
          style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer' }}>
          {(data.annees_disponibles || [annee]).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '16px' }}>
        <span style={{ fontSize: '30px', fontWeight: 600, color: VERT }}>{data.total}</span>
        <span style={{ fontSize: '13px', color: '#6B7280' }}>installation{data.total > 1 ? 's' : ''} en {data.annee}</span>
      </div>

      <div ref={zone} onMouseMove={bouger} onMouseLeave={() => setInfo(null)} style={{ position: 'relative', width: '100%' }}>
        <svg viewBox={`0 0 ${L} ${H}`} style={{ width: '100%', height: '340px', display: 'block', overflow: 'visible' }}>
          <defs>
            <linearGradient id="degradeInstall" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={VERT} stopOpacity="0.22" />
              <stop offset="100%" stopColor={VERT} stopOpacity="0" />
            </linearGradient>
          </defs>

          {graduations.map((v) => (
            <g key={v}>
              <line x1={mg.g} y1={y(v)} x2={L - mg.d} y2={y(v)} stroke="#F3F4F6" strokeWidth="1" />
              <text x={mg.g - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#9CA3AF">{v}</text>
            </g>
          ))}

          {aire && <path d={aire} fill="url(#degradeInstall)" />}
          <path d={d} fill="none" stroke={VERT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {pts.map(([px, py], i) => (
            <circle key={i} cx={px} cy={py} r={info?.i === i ? 6 : 4} fill={VERT} stroke="#fff" strokeWidth="2" />
          ))}

          {valeurs.map((v, i) => (
            <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="11" fill={info?.i === i ? '#065F46' : '#9CA3AF'}>
              {MOIS[i]}
            </text>
          ))}

          {info && <line x1={x(info.i)} y1={mg.h} x2={x(info.i)} y2={mg.h + hauteur} stroke="#D1FAE5" strokeWidth="1.5" />}
        </svg>

        {info && (
          <div style={{ position: 'absolute', top: 0, left: `${(x(info.i) / L) * 100}%`, transform: 'translateX(-50%)', background: '#065F46', color: '#fff', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {MOIS[info.i]} : {info.v} install.
          </div>
        )}
      </div>
    </div>
  );
};

export default OpsGraphInstallations;
